import { windowDimensions } from "src/util/windowWrapper"
import { DOMEvents } from "../../../constants/domEvents"

/**
 * Add Dragging support to in app floating video div
 * @param container
 * @returns
 */
export const enableDragForFloatingVideo = (container: HTMLElement) => {

  let leftPos: number, topPos: number
  let initialLeft = container?.offsetLeft
  let initialTop = container?.offsetTop
  let innerWidth = window.innerWidth
  let innerHeight = windowDimensions.innerHeight

  function dragStart(event: MouseEvent | Touch) {
    // Get the mouse position at the start of the drag.
    leftPos = event.clientX
    topPos = event.clientY

    // Set the initial position of the div element.
    initialLeft = container?.offsetLeft
    initialTop = container?.offsetTop

    // Bind the drag events to the container.
    container.addEventListener(DOMEvents.MOUSEMOVE, handleMousemove)
    container.addEventListener(DOMEvents.MOUSEUP, dragEnd, { passive: true })
    container.addEventListener(DOMEvents.TOUCHMOVE, handleTouchmove)
    container.addEventListener(DOMEvents.TOUCHEND, dragEnd)
    container.addEventListener(DOMEvents.MOUSELEAVE, dragEnd)
  }

  function dragInProgress(event: MouseEvent | Touch) {
    // Calculate the new position of the div element base on drag position.
    const leftMouseDiff = event.clientX - leftPos + initialLeft
    const topMouseDiff = event.clientY - topPos + initialTop

    setFloatingVideoPosition(topMouseDiff, leftMouseDiff)
  }

  function dragEnd() {
    // remove all the events from the container.
    container.removeEventListener(DOMEvents.MOUSEMOVE, handleMousemove)
    container.removeEventListener(DOMEvents.MOUSEUP, dragEnd)
    container.removeEventListener(DOMEvents.TOUCHMOVE, handleTouchmove)
    container.removeEventListener(DOMEvents.TOUCHEND, dragEnd)
    container.removeEventListener(DOMEvents.MOUSELEAVE, dragEnd)
  }

  function handleMousemove(event: MouseEvent) {
    event.preventDefault()
    return dragInProgress(event)
  }

  function handleTouchmove(event: TouchEvent) {
    event.preventDefault()
    return dragInProgress(event.touches[0])
  }

  function handleMouseDown(event: MouseEvent) {
    event.preventDefault()
    dragStart(event)
  }

  function handleTouchStart(event: TouchEvent) {
    event.preventDefault()
    dragStart(event.touches[0])
  }

  function handleWindowResize() {
    // re-position floating div according to new resize window
    if (!initialLeft || !initialTop) return

    // Find new position base on the existing values and current browser size
    const newTop =
      (container?.offsetTop * (windowDimensions.innerHeight - container.offsetHeight)) /
      (innerHeight - container.offsetHeight)
    const newLeft =
      (container?.offsetLeft * (window.innerWidth - container.offsetWidth)) /
      (innerWidth - container.offsetWidth)

    setFloatingVideoPosition(newTop, newLeft)

    innerHeight = windowDimensions.innerHeight
    innerWidth = window.innerWidth
  }

  function clampLeft(value: number) {
    // Prevent the container from being dragged beyond half of the screen.
    return Math.max(
      Math.min(value, window.innerWidth - container.offsetWidth / 2),
      -(container.offsetWidth / 2)
    )
  }

  function clampTop(value: number) {
    // Prevent the container from being dragged beyond half of the screen.
    return Math.max(
      Math.min(value, windowDimensions.innerHeight - container.offsetHeight / 2),
      -(container.offsetHeight / 2)
    )
  }

  function setFloatingVideoPosition(top: number, left: number) {
    const leftMouseDiff = clampLeft(left)
    const topMouseDiff = clampTop(top)

    container.style.left = leftMouseDiff + "px"
    // set bottom position base on top position. because div resize in top direction
    container.style.bottom =
      windowDimensions.innerHeight - topMouseDiff - container.offsetHeight + "px"
  }

  container.addEventListener(DOMEvents.MOUSEDOWN, handleMouseDown)
  container.addEventListener(DOMEvents.TOUCHSTART, handleTouchStart)
  window.addEventListener(DOMEvents.RESIZE, handleWindowResize)

  // return clean up function, that will remove all the listener
  return () => {
    dragEnd()
    container.removeEventListener(DOMEvents.MOUSEDOWN, handleMouseDown)
    container.removeEventListener(DOMEvents.TOUCHSTART, handleTouchStart)
    window.removeEventListener(DOMEvents.RESIZE, handleWindowResize)
  }
}
